/**
 * deviceSyncService — V2.N.4
 *
 * Rassemble les données locales d'un visiteur (wallets monitors,
 * portfolios + holdings, modules de pages activés, préférences UI) et
 * les pousse vers /api/device/sync de façon ASYNCHRONE et NON
 * BLOQUANTE. L'objectif : tout remonter en base même pour les
 * utilisateurs sans compte, pour visibilité admin + portabilité
 * multi-appareils future.
 *
 * Principes :
 *   - device_id = analytics_session_id (réutilise l'identité stable
 *     déjà créée en V2.M).
 *   - Fire-and-forget : un échec de sync ne casse jamais l'UX.
 *   - Debounced : on évite de spammer l'endpoint à chaque micro-
 *     changement (coalesce sur 3s).
 *   - Déclenché : au mount de l'app (1×), après chaque mutation clé
 *     (wallet, token, page toggle), et au login (pour rattacher le
 *     device au compte).
 *
 * ⚠️ Posture privacy : cohérente avec V2.M (décision opérateur). Les
 * adresses de wallets sont envoyées en clair.
 */

import { getOrCreateSessionId } from './analyticsTracker';
import { StorageService } from './storage';
import { PortfolioManager } from './portfolioManager';
import { supabase } from '../lib/supabase/client';

const ENDPOINT = '/api/device/sync';
const DEBOUNCE_MS = 3000;

// Clés localStorage des wallets par monitor (cf. useMonitorWallets).
const MONITOR_WALLET_KEYS: Array<{ chain: string; key: string }> = [
  { chain: 'flux', key: 'fluxnode_wallets' },
  { chain: 'cronos', key: 'cronos_wallets' },
  { chain: 'hedera', key: 'hedera_accounts' },
  { chain: 'thegraph', key: 'thegraph_wallets' },
  { chain: 'algorand', key: 'algorand_addresses' },
  { chain: 'cardano', key: 'cardano_addresses' },
  { chain: 'xdc', key: 'xdc_addresses' },
  { chain: 'vechain', key: 'vechain_addresses' },
  { chain: 'icp', key: 'icp_accounts' },
  { chain: 'axelar', key: 'axelar_addresses' },
];

const PAGE_MODULES_KEY = 'page_modules_enabled';

// ─────────────────────────────────────────────────────────────────────
// Collecte des données locales
// ─────────────────────────────────────────────────────────────────────

interface WalletPayload {
  chain: string;
  address: string;
  label?: string;
  source: 'monitor' | 'portfolio';
}

function collectWallets(): WalletPayload[] {
  const out: WalletPayload[] = [];
  for (const { chain, key } of MONITOR_WALLET_KEYS) {
    try {
      const raw = StorageService.get<string[] | null>(key, null);
      if (Array.isArray(raw)) {
        for (const addr of raw) {
          if (typeof addr === 'string' && addr.trim()) {
            out.push({ chain, address: addr.trim(), source: 'monitor' });
          }
        }
      }
    } catch {
      /* ignore */
    }
  }
  return out;
}

interface PortfolioPayload {
  portfolio_id: string;
  name?: string;
  tokens: Array<{
    coin_id: string;
    symbol?: string;
    display_name?: string;
    positions_count: number;
    total_quantity: number;
  }>;
}

function collectPortfolios(): PortfolioPayload[] {
  try {
    PortfolioManager.initialize();
    const portfolios = PortfolioManager.getAllPortfolios();
    return portfolios.map((p) => ({
      portfolio_id: p.id,
      name: p.name,
      tokens: (p.tokens || []).map((tok) => {
        const positions = (tok as { positions?: Array<{ quantity?: number }> }).positions || [];
        const totalQty = positions.reduce((sum, pos) => sum + (Number(pos.quantity) || 0), 0);
        return {
          coin_id: tok.id,
          symbol: (tok as { displayName?: string }).displayName,
          display_name: (tok as { displayName?: string }).displayName,
          positions_count: positions.length,
          total_quantity: totalQty,
        };
      }),
    }));
  } catch {
    return [];
  }
}

function collectEnabledModules(): Record<string, boolean> | null {
  try {
    const modules = StorageService.get<Record<string, boolean> | null>(PAGE_MODULES_KEY, null);
    return modules && typeof modules === 'object' ? modules : null;
  } catch {
    return null;
  }
}

function collectContext() {
  return {
    app_version: StorageService.get<string>('edouard_last_seen_version', '') || undefined,
    locale: StorageService.get<string>('language', '') || undefined,
    currency: StorageService.get<string>('preferred_currency', '') || undefined,
    theme: StorageService.get<boolean>('darkMode', false) ? 'dark' : 'light',
    user_mode: StorageService.get<string>('user_mode', '') || undefined,
  };
}

// ─────────────────────────────────────────────────────────────────────
// Sync (debounced, fire-and-forget)
// ─────────────────────────────────────────────────────────────────────

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight = false;

async function getCurrentUserId(): Promise<string | null> {
  try {
    // Première lecture : peut renvoyer null si la session n'a pas
    // encore été restaurée depuis localStorage (race au mount).
    const first = await supabase.auth.getSession();
    if (first.data?.session?.user?.id) return first.data.session.user.id;
    // Retry après 300ms pour laisser le client Supabase hydrater.
    await new Promise((r) => setTimeout(r, 300));
    const second = await supabase.auth.getSession();
    return second.data?.session?.user?.id || null;
  } catch {
    return null;
  }
}

async function doSync(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (inFlight) return;
  inFlight = true;

  try {
    const deviceId = getOrCreateSessionId();
    if (!deviceId) return;

    const userId = await getCurrentUserId();
    const wallets = collectWallets();
    const portfolios = collectPortfolios();
    const enabledModules = collectEnabledModules();
    const context = collectContext();

    // Rien à remonter si tout est vide ET pas de compte → on skip
    // (évite de créer des device rows fantômes pour des bots/crawlers
    // qui ne font que charger la home).
    if (
      !userId &&
      wallets.length === 0 &&
      portfolios.length === 0 &&
      !enabledModules
    ) {
      return;
    }

    const body = JSON.stringify({
      device_id: deviceId,
      user_id: userId,
      context,
      wallets,
      portfolios,
      enabled_modules: enabledModules,
    });

    // sendBeacon si possible (survit aux navigations), sinon fetch.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        const blob = new Blob([body], { type: 'application/json' });
        if (navigator.sendBeacon(ENDPOINT, blob)) return;
      } catch {
        /* fall through */
      }
    }

    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    });
  } catch {
    /* Silent — sync ne casse jamais l'UX */
  } finally {
    inFlight = false;
  }
}

/**
 * Déclenche un sync debouncé. À appeler après toute mutation locale
 * pertinente (ajout wallet, token, toggle page, etc.).
 */
export function scheduleDeviceSync(): void {
  if (typeof window === 'undefined') return;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    void doSync();
  }, DEBOUNCE_MS);
}

/**
 * Force un sync immédiat (sans debounce). Utile au login pour
 * rattacher le device au compte sans attendre.
 */
export function syncDeviceNow(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  void doSync();
}
